import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FolderOpen, Search, Upload, Loader2, Trash2, Copy, Check, ExternalLink,
  Image as ImageIcon, FileText, AlertCircle, X, LayoutGrid, List,
} from 'lucide-react';
import { api, formatApiError, absoluteUrl } from '../../lib/api';

const TABS = [
  { id: 'all', label: 'Todos' },
  { id: 'image', label: 'Imágenes' },
  { id: 'document', label: 'Documentos' },
];

const EXT_COLORS = {
  pdf: 'bg-rose-50 text-rose-600 border-rose-200',
  doc: 'bg-sky-50 text-sky-600 border-sky-200', docx: 'bg-sky-50 text-sky-600 border-sky-200',
  xls: 'bg-emerald-50 text-emerald-600 border-emerald-200', xlsx: 'bg-emerald-50 text-emerald-600 border-emerald-200', csv: 'bg-emerald-50 text-emerald-600 border-emerald-200',
  ppt: 'bg-amber-50 text-amber-600 border-amber-200', pptx: 'bg-amber-50 text-amber-600 border-amber-200',
  zip: 'bg-violet-50 text-violet-600 border-violet-200',
};

function humanSize(bytes) {
  if (!bytes) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${u[i]}`;
}

export default function AdminMediaPage() {
  const [files, setFiles] = useState([]);
  const [counts, setCounts] = useState({ all: 0, image: 0, document: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('all');
  const [query, setQuery] = useState('');
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState('');
  const [view, setView] = useState('grid'); // grid | list
  const fileRef = useRef(null);

  async function load() {
    setLoading(true); setError('');
    try {
      const { data } = await api.get('/admin/media');
      setFiles(data.files || []);
      setCounts(data.counts || { all: 0, image: 0, document: 0 });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'No se pudieron cargar los archivos.');
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return files.filter((f) => (
      (tab === 'all' || f.type === tab) &&
      (!q || f.filename.toLowerCase().includes(q))
    ));
  }, [files, tab, query]);

  async function onUpload(e) {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setUploading(true); setError('');
    try {
      for (const f of list) {
        const fd = new FormData();
        fd.append('file', f, f.name);
        await api.post('/admin/media/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      await load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Error al subir el archivo.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function copyUrl(f) {
    const url = absoluteUrl(f.url);
    try { await navigator.clipboard.writeText(url); } catch { /* noop */ }
    setCopied(f.filename);
    setTimeout(() => setCopied((c) => (c === f.filename ? '' : c)), 1800);
  }

  async function remove(f) {
    if (!window.confirm(`¿Eliminar definitivamente "${f.filename}"? Si está en uso en alguna página, dejará de mostrarse. Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete('/admin/media', { params: { filename: f.filename, type: f.type } });
      setFiles((prev) => prev.filter((x) => x.filename !== f.filename));
      setCounts((c) => ({ ...c, all: c.all - 1, [f.type]: c[f.type] - 1 }));
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'No se pudo eliminar.');
    }
  }

  return (
    <div className="space-y-6" data-testid="admin-media-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-charcoal-800 flex items-center gap-2">
            <FolderOpen size={22} className="text-cmic-600" /> Archivos
          </h1>
          <p className="text-charcoal-500 text-sm mt-1 max-w-2xl">
            Biblioteca de medios del sitio. Sube imágenes y documentos, copia su URL única y úsala en cualquier parte de la página. Las imágenes y PDFs aparecen aquí, organizados y con buscador.
          </p>
        </div>
        <div>
          <input ref={fileRef} type="file" multiple
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.txt,.csv,.zip,.svg"
            onChange={onUpload} className="hidden" data-testid="media-upload-input" />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="media-upload-btn"
            className="inline-flex items-center gap-2 bg-cmic-600 hover:bg-cmic-700 text-white px-4 py-2.5 rounded-xl font-semibold text-sm shadow-sm hover:shadow-md transition-all disabled:opacity-60">
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
            {uploading ? 'Subiendo…' : 'Subir archivo'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg p-3 text-sm flex items-center gap-2" data-testid="media-error">
          <AlertCircle size={15} /> {error}
          <button onClick={() => setError('')} className="ml-auto text-rose-400 hover:text-rose-600"><X size={14} /></button>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200">
        <div className="flex gap-1">
          {TABS.map((tt) => (
            <button key={tt.id} onClick={() => setTab(tt.id)} data-testid={`media-tab-${tt.id}`}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === tt.id ? 'border-cmic-600 text-cmic-700' : 'border-transparent text-charcoal-500 hover:text-charcoal-800'}`}>
              {tt.label}
              <span className="ml-1.5 text-[10px] bg-gray-100 rounded-full px-1.5 py-0.5">{counts[tt.id] ?? 0}</span>
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-72">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal-400" />
          <input type="text" placeholder="Buscar por nombre…" value={query} onChange={(e) => setQuery(e.target.value)}
            data-testid="media-search" className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm" />
        </div>
        <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden" data-testid="media-view-toggle">
          <button onClick={() => setView('grid')} data-testid="media-view-grid" title="Vista en cuadrícula"
            className={`p-2 ${view === 'grid' ? 'bg-cmic-500 text-white' : 'text-charcoal-500 hover:bg-gray-50'}`}>
            <LayoutGrid size={15} />
          </button>
          <button onClick={() => setView('list')} data-testid="media-view-list" title="Vista en lista"
            className={`p-2 ${view === 'list' ? 'bg-cmic-500 text-white' : 'text-charcoal-500 hover:bg-gray-50'}`}>
            <List size={15} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-24 flex items-center justify-center"><Loader2 className="animate-spin text-cmic-500" size={32} /></div>
      ) : visible.length === 0 ? (
        <div className="py-20 text-center" data-testid="media-empty">
          <FolderOpen size={40} className="mx-auto text-charcoal-300" />
          <p className="text-charcoal-500 text-sm mt-3">{query ? 'No hay archivos que coincidan con tu búsqueda.' : 'Aún no hay archivos. Sube el primero con el botón “Subir archivo”.'}</p>
        </div>
      ) : (
        view === 'grid' ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="media-grid">
            {visible.map((f) => (
              <MediaCard key={f.filename} file={f} copied={copied === f.filename} onCopy={() => copyUrl(f)} onDelete={() => remove(f)} />
            ))}
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden divide-y divide-gray-100" data-testid="media-list">
            {visible.map((f) => (
              <MediaRow key={f.filename} file={f} copied={copied === f.filename} onCopy={() => copyUrl(f)} onDelete={() => remove(f)} />
            ))}
          </div>
        )
      )}
    </div>
  );
}

function MediaRow({ file, copied, onCopy, onDelete }) {
  const isImage = file.type === 'image' && file.extension !== 'svg';
  const fullUrl = absoluteUrl(file.url);
  const extClass = EXT_COLORS[file.extension] || 'bg-charcoal-50 text-charcoal-500 border-charcoal-200';
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 transition-colors" data-testid={`media-row-${file.filename}`}>
      <div className="w-12 h-12 rounded-lg bg-charcoal-50 flex items-center justify-center overflow-hidden flex-shrink-0">
        {isImage || file.extension === 'svg' ? (
          <img src={fullUrl} alt={file.filename} loading="lazy" className={isImage ? 'w-full h-full object-cover' : 'w-2/3 h-2/3 object-contain'} />
        ) : (
          <span className={`text-[10px] font-bold uppercase border rounded px-1.5 py-0.5 ${extClass}`}>{file.extension || 'file'}</span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-charcoal-800 truncate" title={file.filename}>{file.filename}</p>
        <div className="flex items-center gap-2 text-[11px] text-charcoal-400">
          {file.type === 'image' ? <ImageIcon size={11} /> : <FileText size={11} />}
          <span>{humanSize(file.size_bytes)}</span>
          <span>· {new Date(file.modified_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: '2-digit' })}</span>
        </div>
      </div>
      <button onClick={onCopy} data-testid={`media-copy-${file.filename}`}
        className={`inline-flex items-center justify-center gap-1.5 text-[11px] font-medium rounded-md px-2.5 py-1.5 border transition-colors flex-shrink-0 ${copied ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-cmic-50 text-cmic-700 border-cmic-100 hover:bg-cmic-100'}`}>
        {copied ? <><Check size={12} /> Copiada</> : <><Copy size={12} /> Copiar URL</>}
      </button>
      <a href={fullUrl} target="_blank" rel="noopener noreferrer" title="Abrir" data-testid={`media-open-${file.filename}`}
        className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-gray-200 text-charcoal-500 hover:text-cmic-600 hover:bg-gray-50 flex-shrink-0">
        <ExternalLink size={13} />
      </a>
      <button onClick={onDelete} data-testid={`media-delete-${file.filename}`} title="Eliminar"
        className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-gray-200 text-charcoal-500 hover:text-rose-600 hover:bg-gray-50 flex-shrink-0">
        <Trash2 size={14} />
      </button>
    </div>
  );
}

function MediaCard({ file, copied, onCopy, onDelete }) {
  const isImage = file.type === 'image' && file.extension !== 'svg';
  const fullUrl = absoluteUrl(file.url);
  const extClass = EXT_COLORS[file.extension] || 'bg-charcoal-50 text-charcoal-500 border-charcoal-200';
  return (
    <div className="group bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-testid={`media-card-${file.filename}`}>
      <div className="relative aspect-[4/3] bg-charcoal-50 flex items-center justify-center overflow-hidden">
        {isImage ? (
          <img src={fullUrl} alt={file.filename} loading="lazy" className="w-full h-full object-cover" />
        ) : file.extension === 'svg' ? (
          <img src={fullUrl} alt={file.filename} loading="lazy" className="w-2/3 h-2/3 object-contain" />
        ) : (
          <div className="flex flex-col items-center gap-2 text-charcoal-400">
            <FileText size={34} />
            <span className={`text-[10px] font-bold uppercase border rounded px-1.5 py-0.5 ${extClass}`}>{file.extension || 'file'}</span>
          </div>
        )}
        <button onClick={onDelete} data-testid={`media-delete-${file.filename}`} title="Eliminar"
          className="absolute top-1.5 right-1.5 w-7 h-7 rounded-lg bg-white/90 backdrop-blur text-charcoal-500 hover:text-rose-600 hover:bg-white border border-gray-200 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <Trash2 size={14} />
        </button>
      </div>
      <div className="p-2.5 flex flex-col gap-1.5 flex-1">
        <p className="text-xs font-medium text-charcoal-800 truncate" title={file.filename}>{file.filename}</p>
        <div className="flex items-center gap-2 text-[10px] text-charcoal-400">
          {file.type === 'image' ? <ImageIcon size={11} /> : <FileText size={11} />}
          <span>{humanSize(file.size_bytes)}</span>
          <span>· {new Date(file.modified_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: '2-digit' })}</span>
        </div>
        <div className="flex items-center gap-1.5 mt-auto pt-1.5">
          <button onClick={onCopy} data-testid={`media-copy-${file.filename}`}
            className={`flex-1 inline-flex items-center justify-center gap-1.5 text-[11px] font-medium rounded-md px-2 py-1.5 border transition-colors ${copied ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-cmic-50 text-cmic-700 border-cmic-100 hover:bg-cmic-100'}`}>
            {copied ? <><Check size={12} /> Copiada</> : <><Copy size={12} /> Copiar URL</>}
          </button>
          <a href={fullUrl} target="_blank" rel="noopener noreferrer" title="Abrir" data-testid={`media-open-${file.filename}`}
            className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-gray-200 text-charcoal-500 hover:text-cmic-600 hover:bg-gray-50">
            <ExternalLink size={13} />
          </a>
        </div>
      </div>
    </div>
  );
}
